import React, { useEffect, useMemo, useState } from 'react';
import productService from '../services/productService';
import { ProductResponseDTO } from '../types/product';
import { ApiResponse, PageResponse } from '../types/api';
import alertService from '../services/alertService';
import { useNavigate } from 'react-router-dom';
import StockMovementForm from '../components/stock/StockMovementForm';
import StockCreateForm from '../components/stock/StockCreateForm';
import { Boxes, History } from 'lucide-react';

// Página simplificada para listar produtos ativos e facilitar acesso às movimentações

const StockPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PageResponse<ProductResponseDTO> | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedProdutoId, setSelectedProdutoId] = useState<string>('');
  const [selectedLocalId, setSelectedLocalId] = useState<string>('');
  const [termo, setTermo] = useState<string>('');
  const [sort, setSort] = useState<string>('nome,asc');

  const totalPages = useMemo(() => (data?.totalPages ?? 0), [data]);

  // Bloqueia o scroll do body quando o modal estiver aberto
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [openModal]);

  const load = async () => {
    try {
      setLoading(true);
      const useSearch = termo.trim().length > 0;
      const res: ApiResponse<PageResponse<ProductResponseDTO>> = useSearch
        ? await productService.searchByTerm(termo.trim(), { page, size, sort })
        : await productService.listActive({ page, size, sort });
      if (res.success) {
        setData(res.data);
      } else {
        alertService.error('Falha ao carregar produtos');
      }
    } catch (err) {
      alertService.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, termo, sort]);

  // Página sem filtros: apenas listagem de produtos ativos

  return (
    <div className="space-y-6">
      {/* Header alinhado ao padrão */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Boxes className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Estoque</h1>
            <p className="text-sm sm:text-base text-gray-600">Listando produtos ativos e acessos às movimentações</p>
          </div>
        </div>
        {/* Barra de ações removida conforme padrão: botões pertencem à tela de Movimentações */}
      </div>

      {/* Filtros de produto (sem status) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Termo</label>
            <input
              type="text"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder="Termo de pesquisa (nome, código, descrição)"
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Ordenação</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
            >
              <option value="nome,asc">Nome (A-Z)</option>
              <option value="nome,desc">Nome (Z-A)</option>
              <option value="codigo,asc">Código (asc)</option>
              <option value="codigo,desc">Código (desc)</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              className="w-full px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              onClick={() => setPage(0)}
            >Buscar</button>
          </div>
        </div>
        {/* Aviso de rota removido conforme solicitado */}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto / Código</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-gray-500">Carregando...</td>
              </tr>
            )}
            {!loading && data?.content?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-gray-500">Nenhum item encontrado</td>
              </tr>
            )}
            {!loading && data?.content?.map((p) => (
              <tr key={String(p.id)}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="font-medium text-gray-900">{p.nome || p.id}</div>
                  <div className="text-xs text-gray-500">Código: {p.codigo ?? '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center gap-2">
                  <button
                    className="px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
                    onClick={() => {
                      setSelectedProdutoId(String(p.id));
                      setSelectedLocalId('');
                      setOpenModal(true);
                    }}
                  >Movimentar</button>
                  <button
                    className="px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
                    onClick={() => {
                      const base = `/estoque/movimentacoes/produto/${encodeURIComponent(String(p.id))}`;
                      navigate(base);
                    }}
                  >Histórico</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >Anterior</button>
        <span className="text-sm text-gray-600">Página {page + 1} de {totalPages}</span>
        <button
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >Próxima</button>
        <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="px-2 py-1 border rounded">
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

      {/* Modal de Registro de Movimentação */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setOpenModal(false)}></div>
          <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-semibold">Registrar Movimentação</h2>
              <button className="px-2 py-1 border rounded" onClick={() => setOpenModal(false)}>Fechar</button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-8">
              <StockMovementForm
                defaultProdutoId={selectedProdutoId}
                defaultLocalId={selectedLocalId}
                onSuccess={() => { setOpenModal(false); load(); }}
                onCancel={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Estoque */}
      {openCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setOpenCreateModal(false)}></div>
          <div className="relative bg-white rounded border shadow-lg w-full max-w-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Criar Estoque</h2>
              <button className="px-2 py-1 border rounded" onClick={() => setOpenCreateModal(false)}>Fechar</button>
            </div>
            <StockCreateForm onClose={() => setOpenCreateModal(false)} onSuccess={() => { setOpenCreateModal(false); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StockPage;